import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Typography,
  useTheme,
} from "@mui/material";
import { SupportButton } from "components/snackbars/SupportButton";
import { ProductFeature, ProductIcon } from "data/models";
import { CircleCheck } from "lucide-react";
import { toTitleCase } from "utils/commons";

// Helper: format dollar amount with commas
const formatPrice = (amount: number) =>
  amount.toLocaleString(undefined, { minimumFractionDigits: 0 });

type ProductCardProps = {
  priceId: string | null;
  title: string;
  dollarCost: number | null;
  cadence: "month" | "year";
  selected: boolean;
  disabled: boolean;
  type: "selectable" | "contact";
  features: ProductFeature[];
  handleSelect: (selected: string) => void;
  isCurrent: boolean;
};

export const ProductCard = (props: ProductCardProps) => {
  const theme = useTheme();

  const getIcon = (iconType: ProductIcon) => {
    return <CircleCheck style={{ color: theme.palette.success.main }} />;
    // if (iconType === "info") { // return check icon for clip/batch/apps until we intorduce new plans
    //   return <Info style={{ color: theme.palette.info.main }} />;
    // }
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        minWidth: 240,
        display: "flex",
        flexDirection: "column",
        backgroundColor: `${
          props.isCurrent
            ? theme.palette.action.hover
            : props.selected
            ? theme.palette.action.selected
            : theme.palette.background.paper
        }`,
      }}
    >
      <CardHeader title={toTitleCase(props.title)}></CardHeader>
      <CardContent>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
        >
          {props.type === "contact" ? (
            <Typography variant="h4">Contact us</Typography>
          ) : props.dollarCost === null ? (
            <Typography variant="h3">-</Typography>
          ) : (
            <Typography variant="h3">
              ${formatPrice(props.dollarCost)}
              <Typography component="span" variant="body2">
                {" "}
                per {props.cadence}
              </Typography>
            </Typography>
          )}
        </Box>
        {/* Feature list fills available space */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 1,
            minWidth: 347,
          }}
        >
          {props.features
            .filter((x) => !x.cadence || x.cadence === props.cadence)
            .map((f, index) => (
              <Box
                key={index}
                sx={{
                  paddingX: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {getIcon(f.icon)}
                <Box sx={{ paddingLeft: 3 }}>
                  <Typography variant="body2">{f.text}</Typography>
                </Box>
              </Box>
            ))}
        </Box>
      </CardContent>
      <CardActions
        sx={{
          alignSelf: "center",
          marginTop: "auto",
          marginBottom: 1
        }}
      >
        {props.type === "contact" ? (
          <SupportButton text="Contact" variant="contained" />
        ) : props.selected ? (
          <Button variant="contained" disabled>
            Selected
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={props.disabled}
            onClick={() => props.handleSelect(props.priceId ?? "")}
          >
            {props.isCurrent ? "Current" : "Select"}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
